/**
 * Academy domain — SignalR `/hubs/live` contract (camelCase payloads — the REST
 * exception, FR-031). Kept isolated from the PascalCase REST models.
 * Source of truth: `docs/academy-api.types.ts` (final section) + contracts/realtime-hub.md.
 */
import type {
  DateTimeUtc,
  Guid,
  LiveEventStatusType,
  LiveQuestionStatusType,
  ReactionType,
} from './academy-enums.model';
import type { ReactionCounts } from './academy-responses.model';

/** Hub mount path under the academy service root. */
export const ACADEMY_LIVE_HUB_PATH = '/hubs/live' as const;

/** Connection lifecycle as surfaced to consumers. */
export type HubStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

/** Client → server methods (`connection.invoke(name, ...args)`). */
export interface AcademyHubInvoke {
  JoinEvent: (liveEventId: Guid) => Promise<void>;
  /** Producer studio (backend 013): joins without opening an attendance or counting. */
  JoinAsProducer: (liveEventId: Guid) => Promise<void>;
  LeaveAsProducer: (liveEventId: Guid) => Promise<void>;
  LeaveEvent: (liveEventId: Guid, watchedSeconds: number) => Promise<void>;
  /** `false` when this connection's presence session is already closed (backend round 22). */
  Heartbeat: (liveEventId: Guid, watchedSeconds: number) => Promise<boolean>;
  SendMessage: (liveEventId: Guid, text: string) => Promise<void>;
  SendReaction: (liveEventId: Guid, reactionType: ReactionType) => Promise<void>;
}

/** Server → client events (`connection.on(name, cb)`). Payload keys are camelCase. */
export interface AcademyHubEvents {
  EventStatusChanged: { liveEventId: Guid; status: LiveEventStatusType };
  ViewerCountChanged: { liveEventId: Guid; count: number };
  /**
   * `personName` is '' for an unknown person. The account travels only to whoever may see it:
   * on a single-room live (backend 014) the viewers' copy carries neither `tenantId` nor
   * `tenantName`, and the producers' copy carries both (`tenantName` is null while the account
   * is not in the server's cache). On a live from before 014 everyone gets `tenantId` alone.
   */
  QuestionAdded: {
    liveEventId: Guid;
    questionId: Guid;
    personId: Guid;
    personName: string;
    tenantId?: Guid;
    tenantName?: string | null;
    content: string;
    status: LiveQuestionStatusType;
    upvoteCount: number;
  };
  QuestionUpvoted: { liveEventId: Guid; questionId: Guid; upvoteCount: number };
  QuestionStatusChanged: {
    liveEventId: Guid;
    questionId: Guid;
    status: LiveQuestionStatusType;
    answeredByPersonId: Guid | null;
  };
  /**
   * `personName` is '' for an unknown person; `tenantId`/`tenantName` follow the same rule as
   * `QuestionAdded`. `isProducer` (backend 014) is decided when the message is sent: whoever
   * wrote it passes the live's producer gate — on a Platform live that is any staff member of a
   * platform account, from the studio or from the watch page.
   */
  ChatMessageAdded: {
    chatMessageId: Guid;
    liveEventId: Guid;
    personId: Guid;
    personName: string;
    tenantId?: Guid;
    tenantName?: string | null;
    text: string;
    sentOnUtc: DateTimeUtc;
    isProducer: boolean;
  };
  ChatMessageRemoved: { liveEventId: Guid; chatMessageId: Guid };
  ReactionBroadcast: { liveEventId: Guid; reactionType: ReactionType; personId: Guid };
  ReactionCountsChanged: { liveEventId: Guid; counts: ReactionCounts };
  /**
   * Backend 013 — sent only to the event's producers group (`JoinAsProducer`), one per
   * attendance session (a person in two tabs yields two events).
   */
  ParticipantJoined: {
    liveEventId: Guid;
    liveAttendanceId: Guid;
    personId: Guid;
    personName: string;
    tenantId: Guid;
    /** Account name (backend 014); null while the account is not in the server's cache. */
    tenantName: string | null;
    joinedOnUtc: DateTimeUtc;
  };
  /** Backend 013 — leave, hub disconnect or stale-session sweep; producers group only. */
  ParticipantLeft: {
    liveEventId: Guid;
    liveAttendanceId: Guid;
    personId: Guid;
    personName: string;
    tenantId: Guid;
    tenantName: string | null;
    joinedOnUtc: DateTimeUtc;
    leftOnUtc: DateTimeUtc;
    watchedSeconds: number;
  };
  /**
   * Backend round 24 — a producer muted or unmuted someone in the chat; producers group only.
   * Not sent for a repeated mute/unmute, nor for unmuting someone who was never muted.
   */
  ChatParticipantMuteChanged: {
    liveEventId: Guid;
    personId: Guid;
    personName: string;
    tenantId: Guid;
    tenantName: string | null;
    isMuted: boolean;
  };
}

export type AcademyHubEventName = keyof AcademyHubEvents;

/** Convenience aliases for the event payload shapes (consumed by the realtime store). */
export type EventStatusChangedEvent = AcademyHubEvents['EventStatusChanged'];
export type ViewerCountChangedEvent = AcademyHubEvents['ViewerCountChanged'];
export type QuestionAddedEvent = AcademyHubEvents['QuestionAdded'];
export type QuestionUpvotedEvent = AcademyHubEvents['QuestionUpvoted'];
export type QuestionStatusChangedEvent = AcademyHubEvents['QuestionStatusChanged'];
export type ChatMessageAddedEvent = AcademyHubEvents['ChatMessageAdded'];
export type ChatMessageRemovedEvent = AcademyHubEvents['ChatMessageRemoved'];
export type ReactionBroadcastEvent = AcademyHubEvents['ReactionBroadcast'];
export type ReactionCountsChangedEvent = AcademyHubEvents['ReactionCountsChanged'];
export type ParticipantJoinedEvent = AcademyHubEvents['ParticipantJoined'];
export type ParticipantLeftEvent = AcademyHubEvents['ParticipantLeft'];
export type ChatParticipantMuteChangedEvent = AcademyHubEvents['ChatParticipantMuteChanged'];

/** A live event rejection surfaced to consumers without throwing. */
export interface HubRejection {
  liveEventId: Guid;
}

/**
 * A chat message or a reaction that did not go out: the hub refused it, or there was no
 * connection to send it on. The hub answers a refused message the same way whatever the
 * reason (out of the audience, chat closed, muted, sending too fast), so none is carried.
 */
export interface HubSendRejection {
  liveEventId: Guid;
  kind: 'message' | 'reaction';
}

/**
 * The group re-joined after an automatic reconnect, once the hub accepted it. Hub events sent
 * while the connection was down are not replayed, so a consumer re-reads its REST backlog.
 */
export interface HubRejoin {
  liveEventId: Guid;
  asProducer: boolean;
}

/**
 * Coerce a hub enum field that may arrive as a **name** (string, the REST form) or
 * a **number** (System.Text.Json default over SignalR). Numeric strings are tolerated
 * too. Returns `undefined` for unmappable input rather than leaking a raw number.
 */
export function coerceEnum<T extends string>(
  value: unknown,
  byNumber: Record<number, T>,
): T | undefined {
  if (value == null) return undefined;
  if (typeof value === 'number') return byNumber[value];
  if (typeof value === 'string') {
    // quality-gate: ignore adhoc-format — an enum wire code already matched as digits, not a number to format
    if (/^\d+$/.test(value)) return byNumber[Number(value)];
    return value as T;
  }
  return undefined;
}
